import { User } from '../user/user.model';

export type LocalStorage = {
    firebaseUser?: User|null,
    appUser: string|null,
    appOrg: string|null
};

export enum OnDialogCloseEnum {
    LOGOUT,
    BACK
}

enum ErrorOriginEnum {
    CLIENT  = 'CLIENT',
    SERVER  = 'SERVER',
    UNKNOWN = 'UNKNOWN'
}

export class ErrorContext {
    errorName:     string;
    displayName?:  string;
    email?:        string;
    userHeader:    string;
    userMessage:   string;
    url?:          string;
    headers?:      Headers;
    params?:       any;
    data?:         unknown;
    sessionData?:  LocalStorage;
    location?:     string;
    serverMessage?:string
    origin:        ErrorOriginEnum = ErrorOriginEnum.UNKNOWN;

    constructor(ctx: Partial<ErrorContext>) {
        Object.assign(this, ctx);
        if (this.userHeader == null)
            this.userHeader = 'Internal Error';

        if (this.userMessage == null)
            this.userMessage =  'Please contact support or try again later.';

        this.sessionData = JSON.parse(localStorage.getItem('StaitmentLocalStorage') ?? '{}');
        this.displayName = this.sessionData?.firebaseUser?.fullName ?? '';
        this.email = this.sessionData?.firebaseUser?.email ?? '';
        delete this.sessionData!.firebaseUser;

        this.location = this.location ?? window.location.href;
    }
}

export class SwipesumError extends Error {
    ctx: ErrorContext;
    reported = false;
    onDialogClose: OnDialogCloseEnum|undefined = undefined;
    constructor(msg: string, ctx: Partial<ErrorContext> = {}) {
        super(msg);
        this.ctx = new ErrorContext(ctx);
        this.ctx.errorName = this.constructor.name;
    }

    /**
     * Error contains un-exported fields we want to transmit
     */
    printable(): any {
        return { ...this, message: this.message, stack: this.stack };
    }

    /**
     * This function is meant to allow you to upgrade an Error
     * to a more specific Error type. Promotion to a more generic
     * error is not allowed, but promotion to a different branch
     * of the inheritance tree is.
     */
    static upgrade(e: Error & { code?: string }|SwipesumError): SwipesumError {
        // If we have a non-SwipesumError, upgrade
        if (!(e instanceof SwipesumError)) {
            switch (e?.code) {
                case 'auth/requires-recent-login':
                    return AuthorizationError.upgrade(e);
                default:
                    const ret = new this(e.message);
                    ret.stack = e.stack;
                    return ret;
            }
            // If we have a child class of this or AuthenticationError, do not upgrade
        } else if (e instanceof AuthenticationError || (e instanceof this)) {
            return e;
        } 

        // Otherwise, upgrade
        // This is necessary due to a typescript logical bug.
        // It thinks that this === SwipesumError in all cases
        const se = e as SwipesumError;
        const ret =  new this(se.message, se.ctx);
        ret.reported = se.reported;
        // Keep what constructor sets, otherwise defer to original
        ret.onDialogClose = ret.onDialogClose ?? se.onDialogClose;
        ret.stack = se.stack;

        return ret;
    }
}

export class ClientError extends SwipesumError {
    constructor(msg: string, ctx?: Partial<ErrorContext>) {
        super(msg, ctx);
        this.ctx.origin = ErrorOriginEnum.CLIENT;
    }
    //Override
    static upgrade(e: Error & { code?: string }|SwipesumError): SwipesumError {
        // If we have a non-SwipesumError, upgrade
        if (!(e instanceof SwipesumError)) {
            switch (e?.code) {
                case 'auth/requires-recent-login':
                    return AuthorizationError.upgrade(e);
                default:
                    const ret = new this(e.message);
                    ret.stack = e.stack;
                    return ret;
            }
        // Keep error branches distinct
        } else if (e instanceof ServerError || (e instanceof this)) {
            return e;
        } 

        // Otherwise, upgrade
        // This is necessary due to a typescript logical bug.
        // It thinks that this === SwipesumError in all cases
        const se = e as SwipesumError;
        const ret =  new this(se.message, se.ctx);
        ret.reported = se.reported;
        // Keep what constructor sets, otherwise defer to original
        ret.onDialogClose = ret.onDialogClose ?? se.onDialogClose;
        ret.stack = se.stack;

        return ret;
    }
}

export class ServerError extends SwipesumError { 
    constructor(msg: string, ctx?: Partial<ErrorContext>) {
        super(msg, ctx);
        this.ctx.origin = ErrorOriginEnum.SERVER;
    }
    //Override
    static upgrade(e: Error & { code?: string }|SwipesumError): SwipesumError {
        // If we have a non-SwipesumError, upgrade
        if (!(e instanceof SwipesumError)) {
            switch (e?.code) {
                case 'auth/requires-recent-login':
                    return AuthorizationError.upgrade(e);
                default:
                    const ret = new this(e.message);
                    ret.stack = e.stack;
                    return ret;
            }
        // Keep error branches distinct
        } else if (e instanceof AuthenticationError 
            || (e instanceof ClientError)
            || (e instanceof this)) 
        {
            return e;
        } 

        // Otherwise, upgrade
        // This is necessary due to a typescript logical bug.
        // It thinks that this === SwipesumError in all cases
        const se = e as SwipesumError;
        const ret =  new this(se.message, se.ctx);
        ret.reported = se.reported;
        // Keep what constructor sets, otherwise defer to original
        ret.onDialogClose = ret.onDialogClose ?? se.onDialogClose;
        ret.stack = se.stack;

        return ret;
    }
}

export class AuthenticationError extends ServerError { }

export class AuthorizationError extends AuthenticationError {
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);

        this.ctx.userHeader = 'Login Required';
        this.ctx.userMessage = 'You must re-authenticate to complete this action.';
        this.onDialogClose = OnDialogCloseEnum.LOGOUT;
    }

    static override upgrade(e: Error & { code?: string } | SwipesumError): SwipesumError {
        if (!(e instanceof SwipesumError)) {
            const err = new AuthorizationError(e.message);
            err.stack = e.stack;
            return err;
        }
        return super.upgrade(e);
    }
}

export class ForbiddenError extends AuthenticationError { 
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);

        this.ctx.userHeader = 'Forbidden';
        this.ctx.userMessage = 'You do not have access to this resource. Please reach out to support if you believe this is in error.';
        this.onDialogClose = OnDialogCloseEnum.BACK;
    }
}

export class DuplicatePutError extends ServerError { 
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);

        this.ctx.userHeader = 'Error';
        this.ctx.userMessage = 'This action has already been done. You do not need to try again.';
    }
}

export class BadRequestError extends ServerError { }
export class NoContentError  extends ServerError { 
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);
        this.reported = true;
    }
}

// TODO (jacob): Should this be client or server side?
export class NotEnoughCreditsError extends SwipesumError { 
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);

        this.ctx.userHeader = 'PaymentRequired';
        this.ctx.userMessage = 'You must purchase more credits.';
    }
}
